/**
 * InlineConsentGate — Reusable consent-gated wrapper
 *
 * Wraps any data-collection UI element (forms, chatbots, comment boxes)
 * and only renders the child after consent for the required purpose is confirmed.
 *
 * Usage:
 *   <ConsentGate purpose="marketing_email" fallback={<ConsentPrompt />}>
 *     {children}
 *   </ConsentGate>
 *
 * Logic:
 *   - Checks consent status for the required purpose
 *   - If granted: render children
 *   - If not granted or withdrawn: render the fallback (inline consent prompt)
 *   - On grant: re-render children
 */

import { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useConsent,
  CONSENT_PURPOSES,
  getPurposeLabel,
} from "@/hooks/useConsent";
import { acceptPurposes } from "@/services/dpdpApi";
import { toast } from "sonner";

/**
 * Small inline consent prompt shown inside the fallback.
 * Appears when a gated component is blocked.
 */
export function InlineConsentPrompt({ purpose, onGrant, loading }) {
  const purposeInfo = CONSENT_PURPOSES.find((p) => p.code === purpose);

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Lock className="h-3.5 w-3.5 text-amber-600 shrink-0" />
        <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
          Consent required
        </p>
      </div>
      <p className="text-xs text-amber-800 dark:text-amber-300">
        To use this feature, please allow{" "}
        <strong>{getPurposeLabel(purpose)}</strong>:
      </p>
      {purposeInfo && (
        <p className="text-xs text-amber-700 dark:text-amber-400 italic">
          {purposeInfo.description}
        </p>
      )}
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
        onClick={onGrant}
        disabled={loading}
      >
        {loading ? "Granting..." : `Allow ${getPurposeLabel(purpose)}`}
      </Button>
    </div>
  );
}

/**
 * InlineConsentGate — main component
 *
 * @param {string} purpose - Purpose code that must be granted to show children
 * @param {React.ReactNode} fallback - What to show when consent is not granted (default: InlineConsentPrompt)
 * @param {React.ReactNode} children - The gated content
 * @param {boolean} [showPrompt=true] - Whether to show the inline prompt or hide completely
 */
export default function InlineConsentGate({
  purpose,
  fallback: FallbackComponent,
  children,
  showPrompt = true,
}) {
  const { isPurposeGranted, consentStatus, loading, acceptPurposes: accept, activeNotice } =
    useConsent();
  const [granting, setGranting] = useState(false);

  const isGranted = isPurposeGranted(purpose);

  const handleGrant = async () => {
    setGranting(true);
    try {
      await accept([purpose], activeNotice?.notice_id);
      toast.success(`${getPurposeLabel(purpose)} consent granted`);
    } catch (err) {
      toast.error(`Failed to grant consent: ${err.message}`);
    } finally {
      setGranting(false);
    }
  };

  // Loading state — show skeleton or nothing
  if (loading) {
    if (!showPrompt) return null;
    return FallbackComponent ? (
      <FallbackComponent purpose={purpose} onGrant={handleGrant} loading={true} />
    ) : (
      <InlineConsentPrompt purpose={purpose} onGrant={handleGrant} loading={true} />
    );
  }

  // Consent granted — render children
  if (isGranted) {
    return <>{children}</>;
  }

  // Consent not granted — render fallback
  if (!showPrompt) return null;

  return FallbackComponent ? (
    <FallbackComponent purpose={purpose} onGrant={handleGrant} loading={granting} />
  ) : (
    <InlineConsentPrompt purpose={purpose} onGrant={handleGrant} loading={granting} />
  );
}
